/**
 * Console Log Monitor
 * 
 * Monitors console logs to identify frequently repeated debug messages
 * that should be removed or reduced.
 */

interface LogEntry {
  message: string;
  source: string;
  level: string;
  count: number;
  firstSeen: number;
  lastSeen: number;
  samples: string[];
}

class ConsoleMonitor {
  private logs: Map<string, LogEntry> = new Map();
  private originalConsole: {
    log: typeof console.log;
    warn: typeof console.warn;
    error: typeof console.error;
    info: typeof console.info;
    debug: typeof console.debug;
    group: typeof console.group;
    groupEnd: typeof console.groupEnd;
  };
  private enabled: boolean = false;
  private maxSamples: number = 3;
  private reportInterval: number = 30000; // Report every 30 seconds
  private reportTimer: NodeJS.Timeout | null = null;

  constructor() {
    // Store original console methods
    this.originalConsole = {
      log: console.log.bind(console),
      warn: console.warn.bind(console),
      error: console.error.bind(console),
      info: console.info.bind(console),
      debug: console.debug.bind(console),
      group: console.group?.bind(console) || (() => {}),
      groupEnd: console.groupEnd?.bind(console) || (() => {}),
    };
  }

  /**
   * Start monitoring console logs
   */
  start(): void {
    if (this.enabled) return;
    
    this.enabled = true;
    this.logs.clear();

    // Override console methods
    console.log = (...args: any[]) => {
      this.trackLog('log', args);
      this.originalConsole.log(...args);
    };

    console.warn = (...args: any[]) => {
      this.trackLog('warn', args);
      this.originalConsole.warn(...args);
    };

    console.error = (...args: any[]) => {
      this.trackLog('error', args);
      this.originalConsole.error(...args);
    };

    console.info = (...args: any[]) => {
      this.trackLog('info', args);
      this.originalConsole.info(...args);
    };

    console.debug = (...args: any[]) => {
      this.trackLog('debug', args);
      this.originalConsole.debug(...args);
    };

    // Start periodic reporting
    this.startReporting();

    this.originalConsole.log('[ConsoleMonitor] Started monitoring console logs');
  }

  /**
   * Stop monitoring console logs
   */
  stop(): void {
    if (!this.enabled) return;

    this.enabled = false;

    // Restore original console methods
    console.log = this.originalConsole.log;
    console.warn = this.originalConsole.warn;
    console.error = this.originalConsole.error;
    console.info = this.originalConsole.info;
    console.debug = this.originalConsole.debug;

    // Stop reporting
    if (this.reportTimer) {
      clearInterval(this.reportTimer);
      this.reportTimer = null;
    }

    this.originalConsole.log('[ConsoleMonitor] Stopped monitoring console logs');
  }

  /**
   * Track a log entry
   */
  private trackLog(level: string, args: any[]): void {
    if (!this.enabled) return;

    // Extract message pattern (first string argument or first 100 chars of first arg)
    let message = '';
    let source = 'unknown';

    if (args.length > 0) {
      const firstArg = args[0];
      if (typeof firstArg === 'string') {
        message = firstArg;
        // Try to extract source from bracketed prefix like [Source]
        const match = message.match(/^\[([^\]]+)\]/);
        if (match) {
          source = match[1];
          message = message.substring(match[0].length).trim();
        }
      } else {
        // For non-string first arg, use string representation (limited)
        message = String(firstArg).substring(0, 100);
      }
    }

    // Create a key for this log pattern
    const key = `${level}:${source}:${message.substring(0, 200)}`;

    const now = Date.now();
    const entry = this.logs.get(key);

    if (entry) {
      entry.count++;
      entry.lastSeen = now;
      // Keep a few samples of the full message
      if (entry.samples.length < this.maxSamples) {
        const fullMessage = args.map(arg => 
          typeof arg === 'object' ? JSON.stringify(arg).substring(0, 100) : String(arg)
        ).join(' ');
        if (!entry.samples.includes(fullMessage)) {
          entry.samples.push(fullMessage);
        }
      }
    } else {
      this.logs.set(key, {
        message: message.substring(0, 200),
        source,
        level,
        count: 1,
        firstSeen: now,
        lastSeen: now,
        samples: args.length > 0 ? [String(args[0]).substring(0, 200)] : []
      });
    }
  }

  /**
   * Start periodic reporting
   */
  private startReporting(): void {
    if (this.reportTimer) {
      clearInterval(this.reportTimer);
    }

    this.reportTimer = setInterval(() => {
      this.report();
    }, this.reportInterval);
  }

  /**
   * Generate and log a report of frequently repeated logs
   */
  report(): void {
    if (this.logs.size === 0) return;

    // Filter logs that appear more than 3 times
    const frequentLogs = Array.from(this.logs.values())
      .filter(entry => entry.count > 3)
      .sort((a, b) => b.count - a.count)
      .slice(0, 20); // Top 20 most frequent

    if (frequentLogs.length === 0) return;

    this.originalConsole.group(`🔍 Console Monitor Report (${new Date().toLocaleTimeString()})`);
    this.originalConsole.log(`Found ${frequentLogs.length} frequently repeated log patterns:\n`);

    frequentLogs.forEach((entry, index) => {
      const frequency = entry.count;
      const timeSpan = ((entry.lastSeen - entry.firstSeen) / 1000).toFixed(1);
      const rate = (entry.count / ((entry.lastSeen - entry.firstSeen) / 1000)).toFixed(2);

      this.originalConsole.log(
        `%c${index + 1}. [${entry.source}] ${entry.message.substring(0, 80)}${entry.message.length > 80 ? '...' : ''}`,
        'font-weight: bold; color: #f59e0b'
      );
      this.originalConsole.log(
        `   Count: ${frequency} | Time span: ${timeSpan}s | Rate: ${rate}/s | Level: ${entry.level}`
      );
      if (entry.samples.length > 0) {
        this.originalConsole.log(`   Sample: ${entry.samples[0]}`);
      }
      this.originalConsole.log('');
    });

    this.originalConsole.groupEnd();
  }

  /**
   * Get current statistics
   */
  getStats(): {
    totalLogs: number;
    uniquePatterns: number;
    frequentPatterns: number;
    topLogs: Array<{
      source: string;
      message: string;
      count: number;
      level: string;
    }>;
  } {
    const frequentLogs = Array.from(this.logs.values())
      .filter(entry => entry.count > 3)
      .sort((a, b) => b.count - a.count)
      .slice(0, 10);

    return {
      totalLogs: Array.from(this.logs.values()).reduce((sum, entry) => sum + entry.count, 0),
      uniquePatterns: this.logs.size,
      frequentPatterns: frequentLogs.length,
      topLogs: frequentLogs.map(entry => ({
        source: entry.source,
        message: entry.message,
        count: entry.count,
        level: entry.level
      }))
    };
  }

  /**
   * Clear all tracked logs
   */
  clear(): void {
    this.logs.clear();
  }
}

// Create singleton instance
export const consoleMonitor = new ConsoleMonitor();

// Auto-start in development mode
if (import.meta.env.DEV && typeof window !== 'undefined') {
  // Wait a bit for app to initialize
  setTimeout(() => {
    consoleMonitor.start();
    console.log('[ConsoleMonitor] Auto-started in development mode');
  }, 2000);
}

// Expose to window for manual control
if (typeof window !== 'undefined') {
  (window as any).consoleMonitor = consoleMonitor;
}
